import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, CheckCircle, Mail,
  BarChart2, TrendingUp, MousePointer, Zap, DollarSign, Star,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PageHero from "./PageHero";
import PortfolioFooter from "./PortfolioFooter";

const gradientText = "bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent";
const gradientBg   = "bg-gradient-to-r from-purple-700 to-pink-600";
const iconBg       = "bg-purple-50 border border-purple-100";
const iconColor    = "text-purple-500";
const checkColor   = "text-purple-500";
const statsBg      = "bg-purple-900";
const ctaBg        = "bg-purple-950";
const sectionAlt   = "bg-purple-50";

const services = [
  {
    icon: <MousePointer className={`w-7 h-7 ${iconColor}`} />,
    title: "Pay-Per-Click (PPC) Advertising",
    desc: "ROI-focused Google Ads, Meta Ads, and LinkedIn campaigns that put your brand in front of decision-makers precisely when they're searching.",
    features: ["Google Search & Display campaigns", "Meta & LinkedIn paid ads", "Keyword research & bid management", "Conversion tracking & optimisation"],
  },
  {
    icon: <Mail className={`w-7 h-7 ${iconColor}`} />,
    title: "Email Marketing & Automation",
    desc: "Personalised email sequences and drip campaigns that nurture leads, re-engage dormant customers, and drive repeat revenue.",
    features: ["Campaign strategy & copywriting", "Audience segmentation", "Automated drip sequences", "Open rate & CTR optimisation"],
  },
  {
    icon: <TrendingUp className={`w-7 h-7 ${iconColor}`} />,
    title: "Conversion Rate Optimisation",
    desc: "We audit your funnels, run A/B tests, and redesign landing pages to turn more of your existing traffic into paying customers.",
    features: ["Landing page A/B testing", "Heatmap & session analysis", "Funnel drop-off identification", "UX-driven page redesign"],
  },
  {
    icon: <BarChart2 className={`w-7 h-7 ${iconColor}`} />,
    title: "Performance Analytics & Reporting",
    desc: "End-to-end attribution reporting so you know exactly which channels, campaigns, and creatives are driving revenue.",
    features: ["GA4 & pixel setup & audit", "Multi-channel attribution", "Monthly ROI dashboards", "Campaign spend recommendations"],
  },
];

const channels = [
  { name: "Google Ads",        color: "bg-blue-600" },
  { name: "Meta Ads",          color: "bg-blue-500" },
  { name: "LinkedIn Ads",      color: "bg-blue-700" },
  { name: "Email Marketing",   color: "bg-purple-700" },
  { name: "Display Network",   color: "bg-pink-600" },
  { name: "YouTube Ads",       color: "bg-purple-600" },
];

const stats = [
  { value: "4.5×", label: "Average ROAS",         icon: <DollarSign className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "55%",  label: "Lower Cost per Lead",  icon: <TrendingUp className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "200+", label: "Campaigns Launched",   icon: <Star className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "30d",  label: "Avg Time-to-Results",  icon: <Zap className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
];

const whyUs = [
  "Certified Google & Meta Ads specialists",
  "Data-first approach — no vanity metrics",
  "Weekly optimisation cycles on all campaigns",
  "Full transparency on budget & ad spend",
  "Dedicated performance marketing manager",
  "Custom dashboards for real-time visibility",
];

const DigitalMarketing = () => {
  const navigate = useNavigate();

  return (
    <div className="font-sans">
      <Helmet>
        <title>Digital Marketing | Winsil Evolution</title>
        <meta name="description" content="PPC, email marketing, CRO and performance analytics. Winsil Evolution drives measurable growth through data-driven digital marketing campaigns." />
      </Helmet>

      <PortfolioNavbar />

      <PageHero
        label="Branding — Digital Marketing"
        labelClass={`font-black ${gradientText}`}
        title="Digital Marketing"
        subtitle="Performance-driven campaigns across Google, Meta, and email that turn ad spend into measurable business growth."
        image="/images/photo-1460925895917-afdab827c52f.jpg"
        bgClass="bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"
        glowColor="#d946ef"
        titleClass="text-white"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className={`text-xs font-black uppercase tracking-[0.25em] text-center mb-2 ${gradientText}`}>Results in Action</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Data-Driven Marketing at Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1551836022-d5d88e9218df.jpg", alt: "Digital marketing analytics meeting", label: "Campaign Analytics" },
              { src: "/images/photo-1559526324-4b87b5e36e44.jpg", alt: "PPC advertising strategy", label: "PPC Advertising" },
              { src: "/images/photo-1521737711867-e3b97375f902.jpg", alt: "Marketing team strategy session", label: "Strategy Sessions" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1460925895917-afdab827c52f.jpg", alt: "Performance marketing dashboard", label: "Performance Dashboards" },
              { src: "/images/photo-1553484771-371a605b060b.jpg", alt: "Email marketing campaign", label: "Email Marketing" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Services Grid ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className={`text-xs font-black uppercase tracking-[0.25em] text-center mb-2 ${gradientText}`}>What We Offer</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">Our Digital Marketing Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((s, i) => (
              <div key={i} className="group bg-gray-50 border border-gray-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className={`h-1 -mx-8 -mt-8 mb-7 rounded-t-2xl ${gradientBg}`} />
                <div className={`w-14 h-14 ${iconBg} rounded-xl flex items-center justify-center mb-5 group-hover:bg-purple-100 transition-colors duration-300`}>
                  {s.icon}
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{s.desc}</p>
                <ul className="space-y-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center space-x-2">
                      <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 ${checkColor}`} />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Channels ── */}
      <div className={`py-16 px-4 sm:px-6 lg:px-8 ${sectionAlt}`}>
        <div className="max-w-7xl mx-auto text-center">
          <p className={`text-xs font-black uppercase tracking-[0.25em] mb-2 ${gradientText}`}>Channels</p>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-10">Platforms We Advertise On</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {channels.map((c) => (
              <div key={c.name} className={`${c.color} text-white px-7 py-3 rounded-full text-sm font-black shadow-md hover:scale-105 transition-transform duration-200 cursor-default`}>
                {c.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className={`py-16 px-4 sm:px-6 lg:px-8 ${statsBg}`}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          {stats.map((s) => (
            <div key={s.label}>
              {s.icon}
              <p className="text-4xl sm:text-5xl font-black">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/50 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Why Us ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group order-2 lg:order-1">
              <img
                src="/images/photo-1553484771-371a605b060b.jpg"
                alt="Digital marketing analytics"
                className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/60 via-transparent to-pink-500/10" />
              <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className={`text-2xl font-black ${gradientText}`}>4.5×</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Avg ROAS</p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className={`text-xs font-black uppercase tracking-[0.25em] mb-3 ${gradientText}`}>Why Winsil</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
                Marketing Spend That{" "}
                <span className={gradientText}>Pays Back</span>
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                We treat your ad budget like our own. Every campaign is built on data, every
                decision backed by numbers, and every rupee tracked to a real business outcome.
              </p>
              <ul className="space-y-3 mb-10">
                {whyUs.map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <CheckCircle className={`w-4 h-4 flex-shrink-0 ${checkColor}`} />
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/contact")}
                className={`inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg ${gradientBg} text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/25`}
              >
                Launch My Campaign
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className={`relative ${ctaBg} py-16 px-4 overflow-hidden text-white text-center`}>
        <div className={`absolute inset-0 opacity-20 ${gradientBg}`} />
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative z-10 max-w-xl mx-auto">
          <p className={`text-[11px] font-black uppercase tracking-[0.25em] mb-3 ${gradientText}`}>Ready to Scale?</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Turn Ad Spend Into Revenue</h2>
          <p className="text-white/55 mb-8">Get a free audit of your current campaigns and a 30-day growth plan.</p>
          <button
            onClick={() => navigate("/contact")}
            className={`inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-full ${gradientBg} text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30`}
          >
            Get My Free Audit
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default DigitalMarketing;
